import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIc, ChevronUpIc } from '@dsch/dd-icons';
import { type OpeningHourProps, OpeningHours } from './OpeningHours';
import {
  getOpeningHoursForDay,
  isAppointmentOnly,
  updateOpeningHours,
} from '../../../helpers/openingHours';

const ListRow = styled.button<{ isOpen?: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme, isOpen }) =>
    isOpen ? theme.spacing.S8 : theme.spacing.M16};
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.donedeal.colors.BLUE};
    outline-offset: 2px;
  }
`;

const Key = styled.p<{ isOpen?: string }>`
  ${({ theme }) => theme.donedeal.fontSize.B14};
  color: ${({ theme, isOpen }) => {
    if (isOpen === 'Open Now') {
      return theme.donedeal.colors.GREEN_DARK;
    } else if (isOpen === 'Closed') {
      return theme.donedeal.colors.RED_DARK;
    }
    return theme.donedeal.colors.GREY_DARK;
  }};
  margin-right: ${({ theme }) => theme.spacing.M16};
`;
const Value = styled.p`
  ${({ theme }) => theme.donedeal.fontSize.B14};
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.donedeal.fontWeight.bold};
  word-break: break-all;
  text-align: right;
`;

const SOpeningHours = styled(OpeningHours)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const DealerHours = ({
  openingHours,
}: {
  openingHours: OpeningHourProps[] | null;
}) => {
  const [isDisplayTimesOpen, setIsDisplayTimesOpen] = useState<boolean>(false);

  if (!openingHours) {
    return null;
  }

  const appointmentOnly = isAppointmentOnly(openingHours);

  const { mappedOpeningHours: updatedOpeningHours, displayTime } =
    updateOpeningHours(openingHours);

  return (
    <>
      <ListRow
        type="button"
        onClick={() => setIsDisplayTimesOpen(!isDisplayTimesOpen)}
        disabled={appointmentOnly || updateOpeningHours.length === 0}
        isOpen={isDisplayTimesOpen}
        aria-expanded={isDisplayTimesOpen}
        aria-controls="dealer-hours-schedule"
        aria-label="Click to view full schedule"
      >
        <Key isOpen={displayTime}>{displayTime}</Key>
        <Value>
          {getOpeningHoursForDay(openingHours)}{' '}
          {!appointmentOnly &&
            (isDisplayTimesOpen ? (
              <ChevronUpIc width="16" height="16" aria-hidden="true" />
            ) : (
              <ChevronDownIc width="16" height="16" aria-hidden="true" />
            ))}
        </Value>
      </ListRow>
      {isDisplayTimesOpen && (
        <SOpeningHours
          id="dealer-hours-schedule"
          aria-label="dealer-hours-schedule"
          openingHours={updatedOpeningHours}
        />
      )}
    </>
  );
};

export { DealerHours };
